import { CLASS_NAMES } from "@/core/constants/class-names";
import { PhotoTrigger } from "@/spiffs/spiffs.d";
import { DetailedUser } from "@/user/user";
import {
  SPIFF_RULE_EVENTS,
  SPIFF_RULE_EVENTS_DESCRIPTIONS,
  SPIFF_RULE_EVENTS_LABELS,
  SPIFF_RULE_EVENTS_ORDER,
  SHOWN_SPIFF_RULES,
} from "./constants";
import { SpiffRule, SpiffRuleEvent } from "./spiff-rule";
import { PotentialSpiffTrigger } from "./spiff-rule-trigger";

/**
 * Returns spiff rule events in the defined order, filtering to only include events that exist in the provided array
 * @param events - Array of spiff rule events to filter and order
 * @returns Ordered array of events
 */
export const getOrderedSpiffRuleEvents = (
  events: SpiffRuleEvent[],
): SpiffRuleEvent[] => {
  const eventSet = new Set(events);
  return SPIFF_RULE_EVENTS_ORDER.filter((event) => eventSet.has(event));
};

export const getSpiffTriggerWithUser = (
  trigger: PotentialSpiffTrigger,
  users?: DetailedUser[],
) => {
  if (trigger.class_name === CLASS_NAMES.SpiffPhoto) {
    const photoTrigger = trigger as PhotoTrigger;
    return {
      ...trigger,
      user: users?.find((user) => user.id === photoTrigger.user_id),
    };
  }
  return trigger;
};

export interface SpiffRuleDisplay {
  label: string;
  description: string;
}

/**
 * Get the label and description for a spiff rule, handling special cases like pricebook items
 * that need dynamic content based on metadata.
 *
 * @param rule - The spiff rule to generate display content for
 * @returns An object with label and description strings
 */
export const getSpiffRuleDisplay = (rule: SpiffRule): SpiffRuleDisplay => {
  // Handle pricebook item sales with custom display name
  if (rule.event === SPIFF_RULE_EVENTS.pricebook_item_sale) {
    const displayName = rule.metadata?.pricebook_item_display_name;
    if (displayName) {
      return {
        label: displayName,
        description: `Pricebook spiff for any time ${displayName} is on an invoice of a completed job.`,
      };
    }
  }

  // For all other events (or pricebook without metadata), return the static values
  return {
    label: SPIFF_RULE_EVENTS_LABELS[rule.event],
    description: SPIFF_RULE_EVENTS_DESCRIPTIONS[rule.event],
  };
};

/**
 * Sorts spiff rules by the defined order in SPIFF_RULE_EVENTS_ORDER.
 * Rules with events not in the order list will be placed at the end.
 *
 * @param rules - Array of spiff rules to sort
 * @returns Sorted array of spiff rules
 */
export const sortSpiffRulesByOrder = (
  a: SpiffRuleEvent,
  b: SpiffRuleEvent,
): number =>
  SPIFF_RULE_EVENTS_ORDER.indexOf(a) - SPIFF_RULE_EVENTS_ORDER.indexOf(b);

export const isShownSpiffRule = (rule: SpiffRule): boolean =>
  SHOWN_SPIFF_RULES.includes(rule.event as (typeof SHOWN_SPIFF_RULES)[number]);
